import { useMemo, useState } from 'react';
import { useBundleRanking } from '../../hooks/useCalculations';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { formatPercent, formatNumber, formatCurrencyCompact, pnlColorClass } from '../../lib/formatters';
import { FEATURE_NAMES } from '../../data/conjointData';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';

export function BundleOptimizer() {
  const [expanded, setExpanded] = useState(false);
  const bundles = useBundleRanking();
  const pkg0 = useSimulatorStore(s => s.packages[0]);
  const currentFeatureSet = useMemo(() => new Set(pkg0.enabledFeatures), [pkg0.enabledFeatures]);
  const top10 = bundles.slice(0, 10);
  const bottom5 = bundles.slice(-5);
  const hiddenCount = bundles.length - 15;

  function isCurrentBundle(features: string[]): boolean {
    if (features.length !== currentFeatureSet.size) return false;
    return features.every(f => currentFeatureSet.has(f));
  }

  const featureAbbrev = FEATURE_NAMES.map(n => n.split(' ')[0].slice(0, 3).toUpperCase());

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Optimal Feature Combinations</h3>
        <InfoButton helpKey="bundleOptimizer" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">All 128 ON/OFF combinations ranked by Scenario P&L. {expanded ? 'Showing all.' : 'Showing top 10 and bottom 5.'}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border text-secondary">
              <th className="text-left py-2 px-3 font-medium">#</th>
              <th className="text-left py-2 px-3 font-medium">Features</th>
              <th className="text-right py-2 px-3 font-medium">Lift</th>
              <th className="text-right py-2 px-3 font-medium">Rate</th>
              <th className="text-right py-2 px-3 font-medium">Units (scenario)</th>
              <th className="text-right py-2 px-3 font-medium">Scenario P&L</th>
            </tr>
          </thead>
          <tbody>
            {expanded ? (
              bundles.map((b, i) => <BundleRow key={i} rank={i + 1} bundle={b} isCurrent={isCurrentBundle(b.features)} featureAbbrev={featureAbbrev} />)
            ) : (
              <>
                {top10.map((b, i) => <BundleRow key={i} rank={i + 1} bundle={b} isCurrent={isCurrentBundle(b.features)} featureAbbrev={featureAbbrev} />)}
                <tr>
                  <td colSpan={6} className="py-1">
                    <button
                      onClick={() => setExpanded(true)}
                      className="w-full py-1.5 text-center text-xs text-chart-teal hover:text-teal-400 transition-colors flex items-center justify-center gap-1"
                    >
                      <ChevronDown size={14} />
                      Show all {bundles.length} combinations ({hiddenCount} hidden)
                    </button>
                  </td>
                </tr>
                {bottom5.map((b, i) => <BundleRow key={`bottom-${i}`} rank={bundles.length - 4 + i} bundle={b} isCurrent={isCurrentBundle(b.features)} featureAbbrev={featureAbbrev} />)}
              </>
            )}
          </tbody>
        </table>
        {expanded && (
          <button
            onClick={() => setExpanded(false)}
            className="w-full py-2 text-center text-xs text-chart-teal hover:text-teal-400 transition-colors flex items-center justify-center gap-1 mt-1"
          >
            <ChevronUp size={14} />
            Collapse to top 10 and bottom 5
          </button>
        )}
      </div>
    </div>
  );
}

function BundleRow({ rank, bundle, isCurrent, featureAbbrev }: {
  rank: number;
  bundle: { features: string[]; totalLift: number; adjustedChoiceRate: number; s2Units: number; pnl: number };
  isCurrent: boolean;
  featureAbbrev: string[];
}) {
  const featureSet = new Set(bundle.features);
  return (
    <tr className={`border-b border-border/30 transition-colors ${isCurrent ? 'bg-chart-teal/10' : ''}`}
      onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)'; }}
      onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = ''; }}
    >
      <td className="py-2 px-3 text-muted font-mono">{rank}</td>
      <td className="py-2 px-3">
        <div className="flex items-center gap-1.5">
          {FEATURE_NAMES.map((name, fi) => (
            <span key={name} className={`w-5 h-5 rounded text-micro flex items-center justify-center font-mono ${featureSet.has(name) ? 'bg-chart-teal/20 text-chart-teal' : ''}`}
              style={!featureSet.has(name) ? { backgroundColor: 'var(--color-feature-off-bg)', color: 'var(--color-feature-off-text)' } : undefined}
              title={name}
            >{featureAbbrev[fi]}</span>
          ))}
          {isCurrent && <Check size={12} className="text-chart-teal ml-1" />}
        </div>
      </td>
      <td className="text-right py-2 px-3 font-mono text-muted">{(bundle.totalLift * 100).toFixed(1)}%</td>
      <td className="text-right py-2 px-3 font-mono text-primary">{formatPercent(bundle.adjustedChoiceRate)}</td>
      <td className="text-right py-2 px-3 font-mono text-primary">{formatNumber(bundle.s2Units)}</td>
      <td className={`text-right py-2 px-3 font-mono font-semibold ${pnlColorClass(bundle.pnl)}`}>{formatCurrencyCompact(bundle.pnl)}</td>
    </tr>
  );
}
